/*
 * ALOHA QUEEN SUPERNOVA - Pricing
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - NO UNAUTHORIZED USE
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, Check, Sparkles, Loader2, Star, Zap } from 'lucide-react';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';
import DynamicGradientCard from '../components/DynamicGradientCard';
import CopyrightFooter from '../components/CopyrightFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pricing = () => {
  const { user, isAuthenticated, token } = useAuth();
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/tiers`);
      setTiers(response.data);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    }
  };

  const handleUpgrade = async (tier) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/upgrade`,
        { tier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        alert('Failed to create checkout session');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Upgrade failed');
      setLoading(false);
    }
  };
  
  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/create-portal-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Customer Portal
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      alert('Failed to open billing portal');
      setLoading(false);
    }
  };

  const renderPlanCard = (tierKey, tierData) => {
    const isCurrentPlan = user?.subscription_tier === tierKey;
    const isPremium = ['PRO', 'ENTERPRISE'].includes(tierKey);
    
    return (
      <div
        key={tierKey}
        className={`bg-white/10 backdrop-blur rounded-2xl p-8 border-2 ${
          isPremium ? 'border-yellow-500' : 'border-white/20'
        } hover:scale-105 transition transform relative`}
        data-testid={`plan-card-${tierKey.toLowerCase()}`}
      >
        {isPremium && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
              <Sparkles className="w-4 h-4" />
              Most Popular
            </span>
          </div>
        )}
        
        <div className="text-center mb-6">
          <h3 className="text-3xl font-bold text-white mb-2">{tierData.name}</h3>
          <div className="flex items-baseline justify-center gap-1 mb-4">
            <span className="text-5xl font-bold text-white">${tierData.price}</span>
            <span className="text-gray-400">/month</span>
          </div>
          
          {isCurrentPlan && (
            <span className="inline-block bg-green-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
              Current Plan
            </span>
          )}
        </div>

        <ul className="space-y-3 mb-8">
          {tierData.features?.avatarChat && (
            <li className="flex items-start gap-2 text-white">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="font-semibold">3D Avatar Chat Enabled</span>
            </li>
          )}
          {tierData.features?.voiceSync && (
            <li className="flex items-start gap-2 text-white">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span>Real-time Voice & Lip Sync</span>
            </li>
          )}
          {tierData.features?.emotionDetection && (
            <li className="flex items-start gap-2 text-white">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span>Emotion Detection</span>
            </li>
          )}
          <li className="flex items-start gap-2 text-gray-300">
            <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>Deployments: {tierData.features?.deployments === -1 ? 'Unlimited' : tierData.features?.deployments}</span>
          </li>
          <li className="flex items-start gap-2 text-gray-300">
            <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>Domains: {tierData.features?.domains === -1 ? 'Unlimited' : tierData.features?.domains}</span>
          </li>
          <li className="flex items-start gap-2 text-gray-300">
            <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>Chat Messages: {tierData.limits?.chatMessagesPerDay === -1 ? 'Unlimited' : `${tierData.limits?.chatMessagesPerDay}/day`}</span>
          </li>
          {tierData.features?.avatarChat && (
            <>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Avatar Sessions: {tierData.limits?.avatarSessions === -1 ? 'Unlimited' : `${tierData.limits?.avatarSessions}/month`}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Avatar Minutes: {tierData.limits?.avatarSessionMinutes === -1 ? 'Unlimited' : `${tierData.limits?.avatarSessionMinutes}/month`}</span>
              </li>
            </>
          )}
          <li className="flex items-start gap-2 text-gray-300">
            <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>Support: {tierData.features?.support}</span>
          </li>
        </ul>

        {isCurrentPlan && isAuthenticated ? (
          <button
            onClick={handleManageSubscription}
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold transition bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2"
            data-testid={`manage-button-${tierKey.toLowerCase()}`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Manage Subscription'
            )}
          </button>
        ) : (
          <button
            onClick={() => handleUpgrade(tierKey)}
            disabled={loading || isCurrentPlan}
            className={`w-full py-3 rounded-xl font-semibold transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              isPremium
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
            }`}
            data-testid={`upgrade-button-${tierKey.toLowerCase()}`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              isCurrentPlan ? 'Current Plan' : `Upgrade to ${tierData.name}`
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen relative">
      <RealisticGalaxyBackground />
      <div className="relative z-10 container mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Crown className="w-12 h-12 text-yellow-400" />
            <h1 className="text-6xl font-bold text-white">Choose Your Plan</h1>
          </div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Unlock the power of 3D Avatar Chat with Pro and Enterprise plans.
            Start free, upgrade anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {Object.entries(tiers).map(([key, data]) => renderPlanCard(key, data))}
        </div>

        {/* Features Comparison */}
        <div className="mt-20 bg-white/10 backdrop-blur rounded-2xl p-8 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">Why Upgrade to Pro or Enterprise?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">3D Avatar Chat</h3>
              <p className="text-gray-300">
                Experience photorealistic AI avatars that respond with natural facial expressions,
                emotions, and synchronized voice for truly immersive conversations.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-xl font-bold text-purple-400 mb-3">Real-time Voice & Lip Sync</h3>
              <p className="text-gray-300">
                Advanced text-to-speech with perfect lip synchronization using phoneme-based
                mouth shape mapping for lifelike avatar animations.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-xl font-bold text-blue-400 mb-3">Emotion Detection</h3>
              <p className="text-gray-300">
                AI-powered sentiment analysis that adjusts avatar facial expressions to match
                the emotional tone of each response.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-xl font-bold text-green-400 mb-3">Unlimited Usage</h3>
              <p className="text-gray-300">
                Enterprise plan includes unlimited avatar sessions, minutes, and API access
                for seamless integration into your workflows.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
